import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { MfaAuthGuard } from './core/auth/guards/mfaAuth.guard';
import { ResetAuthGuard } from './core/auth/guards/resetAuth.guard';
import { NoAuthGuard } from './core/auth/guards/noAuth.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: 'transactions' },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'transactions',
    },

    // Empty Guard and Check
    {
        path: '',
        canActivate: [ResetAuthGuard],
        canActivateChild: [ResetAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'recover-account',
                loadChildren: () =>
                    import(
                        'app/modules/auth/recover-account/recover-account.routes'
                    ),
            },
            {
                path: 'account',
                loadChildren: () =>
                    import(
                        'app/modules/identity-verfication/identity-verification.routes'
                    ),
            },
            {
                path: 'fatzebra',
                loadChildren: () =>
                    import('app/modules/fatzebra-3ds/fatzebra.routes'),
            },
        ],
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'landing',
                loadChildren: () =>
                    import('app/modules/landing-page/landing.routes'),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.routes'
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'sign-up',
                loadChildren: () =>
                    import('app/modules/onboarding/onboarding.routes'),
            },
            {
                path: 'email-verify',
                loadChildren: () =>
                    import('app/modules/auth/email-verify/email-verify.routes'),
            },
            {
                path: 'mobile-verify',
                loadChildren: () =>
                    import(
                        'app/modules/auth/mobile-verify/mobile-verify.routes'
                    ),
            },
            {
                path: 'change-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/change-password/change-password.routes'
                    ),
            },
            {
                path: 'recover-password-complete',
                loadChildren: () =>
                    import(
                        'app/modules/auth/recover-password-complete/recover-password-complete.routes'
                    ),
            },
            {
                path: 'recover-username-complete',
                loadChildren: () =>
                    import(
                        'app/modules/auth/recover-username-complete/recover-username-complete.routes'
                    ),
            },
            {
                path: 'reset-password-link',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password-link/reset-password-link.routes'
                    ),
            },
        ],
    },

    // MFA Routes
    {
        path: '',
        canActivate: [MfaAuthGuard],
        canActivateChild: [MfaAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'mfa',
                loadChildren: () =>
                    import('app/modules/multifactor/multifactor.routes'),
            },
        ],
    },

    // Session Routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.routes'
                    ),
            },
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'account',
                loadChildren: () =>
                    import(
                        'app/modules/multifactor/setup/multifactor.setup.routes'
                    ),
            },
        ],
    },

    // Authenticated User's routes
    // Admin
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'home',
                loadChildren: () => import('app/modules/home/home.routes'),
            },
            {
                path: 'payments',
                loadChildren: () =>
                    import('app/modules/payments/payments.routes'),
            },
            {
                path: 'fx-payments',
                loadChildren: () =>
                    import('app/modules/fx-payments/fx-payments.routes'),
            },
            {
                path: 'transactions',
                loadChildren: () =>
                    import('app/modules/transactions/transactions.routes'),
            },
            {
                path: 'email-templates',
                loadChildren: () =>
                    import('app/modules/email-template/email-template.routes'),
            },
            {
                path: 'clients',
                loadChildren: () =>
                    import('app/modules/clients/clients.routes'),
            },
            {
                path: 'payees',
                loadChildren: () => import('app/modules/payees/payees.routes'),
            },
            {
                path: 'users',
                loadChildren: () => import('app/modules/users/users.routes'),
            },
            {
                path: 'organisations',
                loadChildren: () =>
                    import('app/modules/organisations/organisations.routes'),
            },
            {
                path: 'incomplete-biller',
                loadChildren: () =>
                    import(
                        'app/modules/incomplete-biller/incomplete-biller.routes'
                    ),
            },
            {
                path: 'accounts',
                loadChildren: () =>
                    import('app/modules/accounts/accounts.routes'),
            },
            {
                path: 'system-jobs',
                loadChildren: () =>
                    import('app/modules/system-jobs/system-jobs.routes'),
            },
            {
                path: 'transfer-funds',
                loadChildren: () =>
                    import(
                        'app/modules/banking/transfer-funds/transfer-funds.routes'
                    ),
            },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'application',
                loadChildren: () =>
                    import(
                        'app/modules/client-pages/application/application.routes'
                    ),
            },
        ],
    },

    // 404 & Catch all
    {
        path: 'page-not-found',
        pathMatch: 'full',
        loadChildren: () => import('app/modules/error/404/error-404.routes'),
    },
    {
        path: 'error',
        children: [
            {
                path: '403',
                loadChildren: () =>
                    import('app/modules/error/403/error-403.routes'),
            },
            {
                path: '500',
                loadChildren: () =>
                    import('app/modules/error/500/error-500.routes'),
            },
        ],
    },
    { path: '**', redirectTo: 'page-not-found' },
];
